/**
 * Owned livestock, drawn in its pen.
 *
 * There is deliberately NO second animal renderer here. The wild herd already has
 * one — `map/fauna.ts` — that streams sprites in and out of a window, y-sorts by
 * feet, mirrors the side art and knows the per-species scale. Pen stock is the
 * same art with a different owner, so it gets its own `Fauna` INSTANCE, seeded
 * from the server's husbandry snapshot instead of from the world seed.
 *
 * Two things are tuned away from the wild:
 *
 *  • No spooking. `spookRadius`/`skittishRadius` are zeroed, so a player walking
 *    up to the barn does not send his own cows sprinting eight tiles across the
 *    farm. Penned animals graze; they do not flee.
 *  • A short leash. Fauna wanders relative to each entity's HOME, and home here is
 *    the pen tile, so `stepTiles: 1` keeps the herd milling on its own doorstep.
 *
 * The instance is kept off `c.fauna` on purpose: `resources.ts` resolves clicks
 * and sword swings against `c.fauna` alone, so your own cow cannot be killed for
 * loot the server never authorised.
 */
import { createFauna, DEFAULT_STATS, DEFAULT_TUNING, loadFaunaTextures, type FaunaTuning, type SpeciesStat } from '../../map/fauna';
import { TILE, type Animal } from '../../map/world';
import type { FarmCtx } from './context';
import type { FarmAnimal } from '../../game-api';

/** Pack variant baked for each livestock species (see /sprites/animals). */
const VARIANT: Record<string, string> = { chicken: 'white', sheep: 'white', cow: 'brown' };

/** Grazing, unkillable, unflappable — the wild stats with the teeth taken out. */
export const PEN_STATS: Record<string, SpeciesStat> = Object.fromEntries(
  Object.entries(DEFAULT_STATS).map(([id, st]) => [id, { ...st, behaviour: 'graze' as const, killable: false }]),
);

export const PEN_TUNING: FaunaTuning = {
  ...DEFAULT_TUNING,
  spookRadius: 0,
  skittishRadius: 0,
  graze: { ...DEFAULT_TUNING.graze, stepTiles: 1 },
  wander: { ...DEFAULT_TUNING.wander, stepTiles: 1 },
};

/**
 * Lay the stock out around its pen tile.
 *
 * The server stores only which PEN an animal belongs to (penTx/penTy) — where it
 * stands is pure presentation, so it is decided here. A ring below and beside the
 * building keeps the herd off the doorway sprite (which is anchored at its base,
 * i.e. drawn upward from the tile) and spreads a full pen of four so they read as
 * four animals rather than one flickering pile.
 */
export function penSpots(animals: FarmAnimal[]): Animal[] {
  const perPen = new Map<string, FarmAnimal[]>();
  for (const a of animals) {
    const k = `${a.penTx},${a.penTy}`;
    (perPen.get(k) ?? perPen.set(k, []).get(k)!).push(a);
  }
  const out: Animal[] = [];
  for (const list of perPen.values()) {
    list.forEach((a, i) => {
      out.push({
        id: a.id,
        species: a.species as Animal['species'],
        variant: VARIANT[a.species] ?? 'white',
        tx: a.penTx + (i % 2 === 0 ? -1 : 1),
        ty: a.penTy + 1 + Math.floor(i / 2),
      });
    });
  }
  return out;
}

/**
 * (Re)build the pen herd. Best-effort ambience, exactly like the villagers: a
 * missing sheet or a torn-down scene must never take the husbandry panel with it.
 */
export async function refreshLivestock(c: FarmCtx, animals: FarmAnimal[]): Promise<void> {
  if (!c.alive || !c.entityLayer) return;
  try {
    c.livestock?.destroy();
    c.livestock = null;
    const spots = penSpots(animals);
    if (!spots.length) return;
    const tex = await loadFaunaTextures(spots);
    if (!c.alive || !c.entityLayer) return;
    c.livestock = createFauna(spots, tex, PEN_STATS, PEN_TUNING, c.entityLayer);
  } catch { /* ambience only */ }
}

/**
 * Nearest penned animal to (wx, wy), in world px — the herder's work node.
 * Reads the LIVE entity positions, not the pen tile, so the herder walks to the
 * cow where it is actually standing.
 */
export function findPenAnimal(c: FarmCtx, wx: number, wy: number, radiusTiles: number): { x: number; y: number } | null {
  if (!c.livestock) return null;
  const maxPx = radiusTiles * TILE;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (const e of c.livestock.entities) {
    const d = Math.hypot(e.x - wx, e.y - wy);
    if (d <= maxPx && d < bestD) { bestD = d; best = { x: e.x, y: e.y }; }
  }
  return best;
}
